import { format } from "date-fns";
import { ChevronRight, ExternalLink, Video } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { cn } from "../../../../../lib/utils";
import { Badge } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import { Separator } from "../../../../../components/ui/separator";
import { ScrollArea } from "../../../../../components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../../../../components/ui/sheet";
import { CompanyLogo, getDomainFromCompany } from "../../../../../components/ui/CompanyLogo";
import MarkdownRenderer from "../../../../../components/agno/MarkdownRenderer";
import { useViews } from "../../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";

export type MetaField = {
  label: string;
  value: any;
  type?: "text" | "date" | "url" | "badge" | "markdown" | "list" | "participants";
  badgeVariant?: "default" | "secondary" | "outline";
};

type AccountInfo = {
  name: string;
  domain?: string;
  externalId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  badge?: { label: string; color?: string };
  fields: MetaField[];
  account?: AccountInfo;
  bodyMarkdown?: string;
  bodyLabel?: string;
  sourceUrl?: string;
  sourceLabel?: string;
  children?: React.ReactNode;
};

function safeFormat(value: any, fmt: string): string {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return format(d, fmt);
  } catch {
    return String(value);
  }
}

function parseList(value: any): string[] {
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === "string" ? v.trim() : ""))
      .filter(Boolean);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    // Try JSON array first
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((v: any) => (typeof v === "string" ? v.trim() : ""))
            .filter(Boolean);
        }
      } catch { /* ignore */ }
    }
    // Comma-separated fallback
    if (trimmed.includes(",")) {
      return trimmed.split(",").map((s) => s.trim()).filter(Boolean);
    }
    return [trimmed];
  }
  return [];
}

const AVATAR_COLORS = [
  "bg-purple-600",
  "bg-blue-600",
  "bg-emerald-600",
  "bg-amber-600",
  "bg-rose-600",
  "bg-cyan-600",
  "bg-indigo-600",
  "bg-teal-600",
];

function getInitials(name: string): string {
  const parts = name.trim().split(/[\s@]+/);
  if (parts.length >= 2 && !name.includes("@")) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.trim().slice(0, 1).toUpperCase();
}

function getDisplayName(value: string): string {
  if (!value.includes("@")) return value;
  const local = value.split("@")[0];
  return local
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function renderParticipants(value: any) {
  const items = parseList(value);
  if (items.length === 0) return <span className="text-muted-foreground">--</span>;

  return (
    <div className="space-y-2">
      {items.map((participant, i) => (
        <div key={`${participant}-${i}`} className="flex items-center gap-3">
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}
          >
            {getInitials(participant)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-foreground truncate">
              {getDisplayName(participant)}
            </p>
            {participant.includes("@") && (
              <p className="text-[12px] text-muted-foreground truncate">
                {participant}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function renderFieldValue(field: MetaField) {
  if (field.value === null || field.value === undefined || field.value === "") {
    return <span className="text-muted-foreground">--</span>;
  }

  switch (field.type) {
    case "date":
      return <span>{safeFormat(field.value, "MMM d, yyyy 'at' h:mm a")}</span>;
    case "url":
      return (
        <a
          href={field.value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline inline-flex items-center gap-1 text-[13px]"
        >
          <span className="truncate max-w-[280px]">{field.value}</span>
          <ExternalLink size={12} />
        </a>
      );
    case "badge":
      return (
        <Badge variant={field.badgeVariant || "secondary"}>
          {String(field.value)}
        </Badge>
      );
    case "markdown":
      return <MarkdownRenderer>{String(field.value)}</MarkdownRenderer>;
    case "participants":
      return renderParticipants(field.value);
    case "list": {
      const items = parseList(field.value);
      if (items.length === 0) return <span className="text-muted-foreground">--</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {items.map((item, i) => (
            <Badge key={i} variant="outline" className="text-xs">
              {item}
            </Badge>
          ))}
        </div>
      );
    }
    default:
      return <span>{String(field.value)}</span>;
  }
}

const ObjectDetailSheet = ({
  open,
  onClose,
  title,
  subtitle,
  badge,
  fields,
  account,
  bodyMarkdown,
  bodyLabel = "Content",
  sourceUrl,
  sourceLabel,
  children,
}: Props) => {
  const fullWidthTypes = new Set(["markdown", "list", "participants"]);
  const navigate = useNavigate();
  const params = useParams();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const { data: viewsData }: any = useViews({ workspaceId: workspace?.id || "" });

  const navigateToAccount = (externalId: string) => {
    const defaultAccountView = viewsData
      ?.filter((v: any) => v?.is_precreated)
      ?.find((v: any) => v?.entity_relation === "accounts");
    const viewId = defaultAccountView?.id;
    if (viewId && params?.workspaceId) {
      onClose();
      navigate(`/home/${params.workspaceId}/view/${viewId}/account/${externalId}/overview`);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="!w-[620px] !max-w-[620px] p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 space-y-3">
          <div className="flex items-start justify-between gap-3 pr-8">
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-base font-semibold text-foreground leading-tight">
                {title}
              </SheetTitle>
              {subtitle && (
                <SheetDescription className="mt-1 text-[13px]">
                  {subtitle}
                </SheetDescription>
              )}
            </div>
            {badge && (
              <Badge variant="secondary" className="shrink-0">
                {badge.label}
              </Badge>
            )}
          </div>
          {sourceUrl && (
            <Button variant="default" size="sm" className="w-fit gap-2" asChild>
              <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
                <Video size={14} />
                {sourceLabel || "Join Meeting"}
              </a>
            </Button>
          )}
        </SheetHeader>

        <Separator />

        <ScrollArea className="flex-1">
          <div className="px-6 py-4 space-y-5">
            {fields.length > 0 && (
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {fields.map((field) => (
                  <div key={field.label} className={fullWidthTypes.has(field.type || "") ? "col-span-2" : ""}>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                      {field.label}
                    </p>
                    <div className="text-[13px] text-foreground">
                      {renderFieldValue(field)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {account?.name && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Account
                  </p>
                  <button
                    type="button"
                    className={cn(
                      "flex items-center gap-3 w-full text-left rounded-lg p-2 -m-2 transition-colors",
                      account.externalId ? "hover:bg-muted cursor-pointer" : "",
                    )}
                    onClick={() => account.externalId && navigateToAccount(account.externalId)}
                    disabled={!account.externalId}
                  >
                    <CompanyLogo
                      domain={account.domain || getDomainFromCompany(account.name)}
                      name={account.name}
                      className="h-8 w-8 rounded"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{account.name}</p>
                      {account.domain && (
                        <p className="text-xs text-muted-foreground">{account.domain}</p>
                      )}
                    </div>
                    {account.externalId && (
                      <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground flex-shrink-0" />
                    )}
                  </button>
                </div>
              </>
            )}

            {bodyMarkdown && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    {bodyLabel}
                  </p>
                  <div className="text-[13px] text-foreground">
                    <MarkdownRenderer>{bodyMarkdown}</MarkdownRenderer>
                  </div>
                </div>
              </>
            )}

            {children && (
              <>
                <Separator />
                {children}
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default ObjectDetailSheet;
